<template>
  <div>
    <b-col class="d-flex justify-content-end px-0 pt-25">
      <filter-wrapper
        :properties="selectedFilters"
        :width-offset="360"
        @clicked="openSelectedFilter"
        @cleared="clearFilters"
      />
      <dropdown
        :title="$tk('ClientPortal.MasterTable.Filters')"
        class="mb-1 mr-1"
        :show-dropdown="showFilters"
        :disabled="isLoading"
        @change="toggleFilters"
      >
        <template #menu-items>
          <dropdown-submenu
            v-for="(filter, index) in model.filters"
            :key="filter.name + '-' + index"
            :input-id="filter.name"
            :text="filter.name"
            input-name="submenu"
            :checked="menu"
            :selected-filter="selectedFilter(filter.name)"
            @changeSelectedMenu="changeMenu"
          >
            <template #items>
              <dropdown-item v-if="filter.name !== 'Statuses'">
                <b-form-input
                  v-model="model.serverParams.columnFilters[filter.name.toLowerCase()]"
                  class="__dropdown-input"
                  :disabled="onChange"
                  :placeholder="filter.name"
                  @input="onInputChange(filter.name)"
                />
              </dropdown-item>
              <template v-if="filter.name==='Statuses'">
                <dropdown-item
                  v-for="(status, statusIndex) in model.statuses"
                  :key="status + '-' + statusIndex"
                  :label="status.text"
                  :filter-name="filter.name"
                  :value="status.value"
                  :menu-name="filter.name"
                  :selected="status.selected"
                  @change="includeOrExclude"
                />
              </template>
              <template v-else>
                <dropdown-item
                  v-for="(option, optionIndex) in filter.operators"
                  :key="option + '-' + optionIndex"
                  type="radio"
                  :label="option"
                  :filter-name="filter.name"
                  :selected="filter.selected"
                  :value="option"
                  :menu-name="filter.name"
                  @change="includeOrExclude"
                />
              </template>
            </template>
            <template #footer>
              <dropdown-clear-item
                :title="$tk('ClientPortal.Filters.Clear')"
                @click="clearFilters(filter.name, false)"
              />
            </template>
          </dropdown-submenu>
          <dropdown-clear-item
            :title="$tk('ClientPortal.Filters.ClearAll')"
            @click="clearFilters"
          />
        </template>
      </dropdown>
      <SearchInputField
        :disabled="isLoading"
        :placeholder="$tk('Admin.Accounts.SearchByName')"
        @onInputSearch="onSearchChange"
      />
    </b-col>
    <section>
      <b-card
        class="my-50 admin-table--management"
        no-body
      >
        <Table
          :data="allAccounts"
          :columns="columns"
          :is-loading="isLoading"
          remote-mode
          class="admin-table__data"
          type-of-table="accounts"
          @changePage="changeCurrentPage"
          @onSortChange="sort"
        />
      </b-card>
    </section>
    <div>
      <b-modal
        id="modal-small-delete"
        :ok-title="$tk('ClientPortal.Form.Actions.Delete')"
        :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
        ok-variant="danger"
        cancel-variant="outline-secondary"
        centered
        size="md"
        :title="$tk('ClientPortal.Admin.DeleteAccount')"
        no-enforce-focus
        @ok="triggerDelete"
      >
        <span>{{ $tk('ClientPortal.Admin.AreYouSureYouWantToDeleteAccount') }}</span>
      </b-modal>
      <!-- Edit account modal -->
      <b-modal
        id="modal-edit-account"
        :title="$tk('ClientPortal.Account.Edit.EditAccount')"
        :ok-title="$tk('ClientPortal.Form.Actions.Save')"
        :cancel-title="$tk('ClientPortal.Form.Actions.Cancel')"
        centered
        cancel-variant="outline-secondary"
        no-enforce-focus
        @show="getAccountModel"
        @ok="updateAccount"
      >
        <b-form-group
          class="mb-2"
          :label="$tk('ClientPortal.Admin.Account.Name')"
          :invalid-feedback="errors['Name']"
        >
          <b-form-input
            v-model="accountModel.name"
            :placeholder="$tk('ClientPortal.Admin.Account.Name')"
            type="text"
            :state="errors['Name'] == null ? null : false"
          />
        </b-form-group>
        <b-form-group
          class="mb-2"
          :label="$tk('ClientPortal.Admin.Account.FiscalNumber')"
        >
          <b-form-input
            v-model="accountModel.fiscalNumber"
            :placeholder="$tk('ClientPortal.Admin.Account.FiscalNumber')"
            type="text"
          />
        </b-form-group>
        <b-form-group
          class="mb-2"
          :label="$tk('ClientPortal.Admin.Account.BusinessNumber')"
        >
          <b-form-input
            v-model="accountModel.businessNumber"
            :placeholder="$tk('ClientPortal.Admin.Account.BusinessNumber')"
            type="text"
          />
        </b-form-group>
        <b-form-group
          class="mb-0"
          :label="$tk('ClientPortal.Admin.Account.PhoneNumber')"
        >
          <b-form-input
            v-model="accountModel.phoneNumber"
            :placeholder="$tk('ClientPortal.Admin.Account.PhoneNumber')"
            type="text"
          />
        </b-form-group>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { VBModal } from 'bootstrap-vue'
import {
  computed,
  onMounted,
  ref,
  reactive,
} from '@vue/composition-api'
import Table from '@/components/Table.vue'
import store from '@/store'
import SearchInputField from '@/components/SearchInputField.vue'
import AccountAdminService from '@/api/admin/accountAdmin.service'
import notification from '@/mixins/toastification'
import Dropdown from '@/components/Dropdown.vue'
import DropdownItem from '@/components/DropdownItem.vue'
import DropdownSubmenu from '@/components/DropdownSubmenu.vue'
import DropdownClearItem from '@/components/DropdownClearItem.vue'
import FilterWrapper from '@/components/FilterWrapper.vue'
import { debounce } from 'vue-debounce'
import translation from '@/mixins/translate'
import onSortChange from '@/mixins/onSortChange'

export default {
  directives: {
    'b-modal': VBModal,
  },
  components: {
    Table,
    SearchInputField,
    Dropdown,
    DropdownItem,
    DropdownSubmenu,
    FilterWrapper,
    DropdownClearItem,
  },
  mixins: [notification],
  setup() {
    const columns = [
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.Id'),
        field: 'id',
        sortable: false,
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.Name'),
        field: 'name',
        width: '300px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.Status'),
        field: 'status',
        width: '120px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.BusinessNumber'),
        field: 'businessNumber',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.Email'),
        field: 'email',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.Address'),
        field: 'address',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.FiscalNumber'),
        field: 'fiscalNumber',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.PhoneNumber'),
        field: 'phoneNumber',
        width: '250px !important',
      },
      {
        label: translation.methods.$tk('ClientPortal.Admin.Account.CreatedAt'),
        field: 'createdAt',
        type: 'date',
        dateInputFormat: 'dd/MM/yyyy HH:mm',
        dateOutputFormat: 'dd/MM/yyyy HH:mm',
        width: '250px !important',
      },
    ]
    const allAccounts = computed(() => store.getters.allAccounts)
    const model = computed(() => store.getters.getAccountModel)
    const isLoading = ref(false)
    const accountModel = ref({})
    const errors = ref({})
    const menu = ref('')
    const showFilters = ref(false)
    const selectedFilters = reactive([])
    const onChange = ref(false)

    const changeCurrentPage = async value => {
      model.value.serverParams.page = value
      await store.dispatch('getFilteredAccounts', model.value)
    }

    const sort = value => {
      onSortChange(value, model, 'getFilteredAccounts')
    }
    onMounted(async () => {
      isLoading.value = true
      await store.dispatch('getAccounts')
      isLoading.value = false
    })

    const onFilterChange = async () => {
      onChange.value = true
      await store.dispatch('getFilteredAccounts', model.value)
      onChange.value = false
    }

    const triggerDelete = async () => {
      const deleteData = store.getters.accountToDelete
      const { accountId } = store.getters['verticalMenu/accountContext']
      try {
        const response = await AccountAdminService.deleteAccount(deleteData)
        if (response.saved) {
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.message)
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getFilteredAccounts', model.value)
        if (accountId === deleteData.id) {
          await store.dispatch('verticalMenu/getDefaultAccount')
        }
      }
    }

    const getAccountModel = () => {
      accountModel.value = store.getters.getEditAccountModel
      errors.value = {}
    }

    const changeMenu = newOpenedMenu => {
      menu.value === newOpenedMenu ? menu.value = '' : menu.value = newOpenedMenu
    }

    const updateAccount = async bvModalEvt => {
      bvModalEvt.preventDefault()
      try {
        const response = await AccountAdminService.updateAccount(accountModel.value)
        if (response.saved) {
          bvModalEvt.vueTarget.hide('modal-edit-account')
          notification.methods.success(response.message)
        } else {
          notification.methods.error(response.errors[Object.keys(response.errors)[0]])
          errors.value = response.errors
        }
      } catch (err) {
        notification.methods.error(err.message)
      } finally {
        await store.dispatch('getFilteredAccounts', model.value)
      }
    }

    const updateSelectedFilters = (filter, count) => {
      const filterObj = selectedFilters.find(f => f.name === filter)
      if (filterObj) {
        if (count > 0) { filterObj.selectedFilters = count } else {
          const filterIndex = selectedFilters.findIndex(f => f.name === filter)
          selectedFilters.splice(filterIndex, 1)
        }
      } else {
        selectedFilters.push({ name: filter, selectedFilters: count })
      }
    }

    const updateStatuses = (filter, value) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      const optionIndex = selectedFilter.value.indexOf(value)
      const statusSelected = model.value.statuses.find(status => status.value === value)
      const { columnFilters } = model.value.serverParams
      if (optionIndex < 0) {
        selectedFilter.value.push(value)
        statusSelected.selected = true
        columnFilters[filter].push(value)
      } else {
        selectedFilter.value.splice(optionIndex, 1)
        statusSelected.selected = false
        columnFilters[filter].splice(optionIndex, 1)
        if (selectedFilter.value.length === 0 && columnFilters[filter].length === 0) {
          delete propertyFilters[filter]
          delete columnFilters[filter]
        }
      }
    }

    const updatePropertyFilter = (filter, value, operator) => {
      const { propertyFilters } = model.value.serverParams
      const selectedFilter = propertyFilters[filter]
      if (selectedFilter.operator === operator) {
        delete propertyFilters[filter]
      } else {
        selectedFilter.operator = operator
        selectedFilter.value = value
      }
    }

    const updatePropertyFilters = (filter, value, operator = null) => {
      const { propertyFilters } = model.value.serverParams
      const filterObject = filter.toLowerCase()
      if (filterObject in propertyFilters) {
        if (filter === 'Statuses') {
          updateStatuses(filterObject, value)
        } else {
          updatePropertyFilter(filterObject, value, operator)
        }
      } else {
        propertyFilters[filterObject] = {
          operator,
          property: filter,
          value: filter === 'Statuses' ? [value] : value,
        }
        if (filter === 'Statuses') {
          const { columnFilters } = model.value.serverParams
          columnFilters[filterObject] = []
          columnFilters[filterObject].push(value)
        }
      }
      const count = filter === 'Statuses' ? propertyFilters[filterObject]?.value.length : 1
      updateSelectedFilters(filter, count)
    }

    const includeOrExclude = (filter, option) => {
      if (filter === 'Statuses') {
        const statusSelected = model.value.statuses.find(status => status.value === option)
        statusSelected.selected === true ? statusSelected.selected = false : statusSelected.selected = true
        updatePropertyFilters(filter, option)
        onFilterChange()
      } else {
        const selectedOption = model.value.filters.find(f => f.name === filter)
        const { columnFilters } = model.value.serverParams
        selectedOption.selected === option ? selectedOption.selected = '' : selectedOption.selected = option
        updatePropertyFilters(filter, columnFilters[filter.toLowerCase()], option)
        if (!columnFilters[filter.toLowerCase()]) { return }
        onFilterChange()
      }
    }

    const toggleFilters = (toogleFilter = null) => {
      menu.value = ''
      showFilters.value = toogleFilter ?? !showFilters.value
    }

    const clearFilters = (filter = null, closeAllDropdowns = true) => {
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      const { statuses } = model.value
      const { filters } = model.value
      if (!filter) {
        selectedFilters.splice(0, selectedFilters.length)
        filters.forEach(f => { f.selected = '' })
        Object.keys(propertyFilters).forEach(propertyFilter => { delete propertyFilters[propertyFilter] })
        Object.keys(columnFilters).forEach(columnFilter => { columnFilters[columnFilter] = null })
        statuses.forEach(status => { status.selected = false })
        toggleFilters(false)
      } else {
        const selectedFilter = model.value.filters.find(f => f.name === filter)
        const includedFilterIndex = selectedFilters.findIndex(f => f.name === filter)
        const filterObject = filter.toLowerCase()
        columnFilters[filterObject] = null
        if (includedFilterIndex > -1) { selectedFilters.splice(includedFilterIndex, 1) }
        selectedFilter.selected = ''
        delete propertyFilters[filterObject]
        if (filter === 'Statuses') { statuses.forEach(status => { status.selected = false }) }
        if (closeAllDropdowns) { toggleFilters(false) }
      }
      onFilterChange()
      menu.value = ''
    }

    const onInputChange = debounce(filter => {
      const filterObject = filter.toLowerCase()
      const { propertyFilters } = model.value.serverParams
      const { columnFilters } = model.value.serverParams
      if (filterObject in propertyFilters) {
        propertyFilters[filterObject].value = columnFilters[filterObject]
      } else { return }
      onFilterChange()
    }, 400)

    const onSearchChange = async value => {
      model.value.serverParams.name = value
      if (!value) { await store.dispatch('getFilteredAccounts', model.value) }
      await store.dispatch('getFilteredAccounts', model.value)
    }

    const selectedFilter = name => {
      const filter = selectedFilters.findIndex(f => f.name === name)
      if (filter > -1) return true
      return false
    }

    const openSelectedFilter = filterName => {
      toggleFilters(true)
      menu.value = filterName
    }

    window.onclick = e => {
      if (!e.path.find(x => x.className?.includes('dropdown'))) {
        toggleFilters(false)
        menu.value = ''
      }
    }

    return {
      allAccounts,
      isLoading,
      changeCurrentPage,
      triggerDelete,
      updateAccount,
      accountModel,
      getAccountModel,
      errors,
      model,
      showFilters,
      menu,
      selectedFilters,
      changeMenu,
      includeOrExclude,
      clearFilters,
      toggleFilters,
      onInputChange,
      onSearchChange,
      columns,
      sort,
      selectedFilter,
      openSelectedFilter,
      onChange,
    }
  },
}
</script>
